import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CounsellingCommonComponent } from '../../../core/common_components/counselling-common/counselling-common.component';
import { TimeFormatPipe } from '../../../core/pipes/TimeFormat.pipe';
import { INVOICE_STATUS, INVOICE_TYPE } from '../../../core/constants/keys.constant';
import { AuthService } from '../../../services/auth.service';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { SpinnerService } from '../../../services/spinner.service';
import { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { StripeService } from '../../../services/stripe.service';
import { ToasterService } from '../../../services/toaster.service';
import { CommonService } from '../../../services/common.service';

@Component({
  selector: 'mindbase-invoice-payout',
  standalone: true,
  imports: [
    CommonModule,
    CounsellingCommonComponent,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule,
    RouterModule,
    FormsModule,
    TimeFormatPipe,
    MatIconModule
  ],
  templateUrl: './invoice-payout.component.html',
  styleUrl: './invoice-payout.component.scss'
})
export class InvoicePayoutComponent implements OnInit, OnDestroy {

  private destroy: Subject<boolean> = new Subject<boolean>();
  protected invoiceId: any;
  protected familyId: any;
  protected invoiceType: any;
  protected origin: any;
  protected invoiceT = INVOICE_TYPE;
  protected invoiceAmount: any;
  protected  clientSecret: string = 'pi_xxx_secret_xxx';
  protected stripe: Stripe | null = null;
  protected card: StripeCardElement | null = null;
  protected saveCard: boolean = true;
  protected isCardComplete: boolean = false;
  protected isPaid: boolean = false;
  
  constructor(
    private route: ActivatedRoute, 
    private _authService: AuthService, 
    private spinnerService: SpinnerService, 
    private _translateService: TranslateService,
    private _toasterService:ToasterService,
    private stripeService: StripeService,
    private commonService: CommonService,
    private router : Router
  ) {
    this.invoiceId = this.route.snapshot.queryParamMap.get('invoiceId');
    this.familyId = this.route.snapshot.queryParamMap.get('familyId');
    this.invoiceAmount = this.route.snapshot.queryParamMap.get('amount');
    this.invoiceType = this.route.snapshot.queryParamMap.get('invoiceType');
    this.origin = this.route.snapshot.queryParamMap.get('origin');
    this.getPaymentStatus(this.invoiceId);
  }

  ngOnInit(): void {
    // Wait for Stripe publishable key from settings before mounting card fields
    this.commonService.settingsSubject
      .pipe(
        filter((settings: any) => !!(
          settings?.tenantInfo?.stripePublishKey &&
          settings.tenantInfo.stripePublishKey !== 'TEST'
        )),
        take(1),
        takeUntil(this.destroy)
      )
      .subscribe(() => this.loadStripe());
  }

  getPaymentStatus(invoiceId: string): void {
    this.spinnerService.addToLoader('getRecentFiles');
    this._authService
      .getPaymentStatus(invoiceId)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: any) => {
          this.spinnerService.removeFromLoader('getRecentFiles');          
          this.isPaid = res.data === INVOICE_STATUS.PAID;
        },
        error: () => {
          this.spinnerService.removeFromLoader('getRecentFiles');
        }
      });
  }

  async loadStripe(): Promise<void> {
    if (this.stripe && this.card) return; // Avoid double mount

    this.stripe = await this.stripeService.getStripe();
    if (this.stripe && document.getElementById('card-element')) {
      const elements = this.stripe.elements();
      this.card = elements.create('card', {
        hidePostalCode: true
      });
      this.card.mount('#card-element');

      // Listen for changes
      this.card.on('change', (event) => {
        this.isCardComplete = event.complete;
      });
    } else {
      console.error('Card element not found or Stripe failed to load.');
    }
  }

  getInvoiceType(type: any): string {
    const normalizedType = isNaN(Number(type)) ? type : Number(type);
    switch (normalizedType) {
    case INVOICE_TYPE.COURSE:
      return 'Course';
    case INVOICE_TYPE.PACKAGE:
      return 'Package';
    case INVOICE_TYPE.SESSION:
      return 'Session';
    case INVOICE_TYPE.OTHERS:
      return 'Others';
    case INVOICE_TYPE.PAYROLL_ENTRY:
      return 'Payroll Entry';
    default:
      return 'Unknown';
    }
  }

  async payOut(): Promise<void> {
    if (!this.stripe || !this.card) {
      await this.loadStripe(); // Ensure Stripe is ready
    }
    if (!this.isCardComplete) {
      this._toasterService.errToastr(
        this._translateService.instant('Please enter valid card details.')
      );
      return;
    }
    const data = {
      familyId : this.familyId,
      invoiceType : this.invoiceType,
      amount : this.invoiceAmount,
      invoiceId : this.invoiceId
    };
    this.spinnerService.addToLoader('makePayment');
    this._authService.invoicePayOut(data).pipe(takeUntil(this.destroy)).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (res: any) => {
        if (res) {
          this.clientSecret = res.data.paymentIntent.client_secret;
          this.handlePayment();
        }
      },
      error: () => {
        this.spinnerService.removeFromLoader('makePayment');
      }
    });
  }

  async handlePayment(): Promise<void> {
    if (!this.stripe || !this.card) return;

    try {
      const result = await this.stripeService.confirmPayment(
        this.clientSecret,
        this.card,
        this.saveCard
      );

      if (result.error) {
        alert(`Payment failed: ${result.error.message}`);
      } else if (result.paymentIntent?.status === 'succeeded') {
        this.spinnerService.removeFromLoader('makePayment');
        this._toasterService.successToastr(
          this._translateService.instant('Invoice Payment Successfully')
        );
        this.router.navigate(['/auth/login']);
      }
    } catch (error) {
      console.error('Error during payment:', error);
    }
  }


  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }

}
